import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { ordersApi } from '../services/api';
import { Order } from '../types';

export default function OrderConfirmationPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError('No order ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await ordersApi.get(orderId);
        // API returns order directly or in response.data
        setOrder(response.data || response);
      } catch (err: any) {
        console.error('Failed to fetch order:', err);
        setError(err.message || 'Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-keder-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-keder-yellow mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-keder-dark flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-900/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Error Loading Order</h2>
          <p className="text-gray-400 mb-6">{error || 'Unable to load order details'}</p>
          <Link
            to="/dashboard"
            className="inline-block px-6 py-3 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
          >
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number | string | undefined) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (num === undefined || isNaN(num)) return '$0.00';
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-keder-dark py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-900/30 flex items-center justify-center">
            <svg className="w-10 h-10 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Order Submitted Successfully!</h1>
          <p className="text-gray-400">Thank you for your order. You will receive a confirmation email shortly.</p>
        </div>

        {/* Order Details Card */}
        <div className="bg-keder-gray rounded-xl border border-gray-700 p-6 mb-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <p className="text-gray-400 text-sm">Order Number</p>
              <p className="text-2xl font-bold text-keder-yellow">{order.orderNumber || order.order_number}</p>
            </div>
            <div className="text-right">
              <p className="text-gray-400 text-sm">Order Date</p>
              <p className="text-white">{formatDate(order.createdAt || order.created_at)}</p>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-4 mb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-400 text-sm">Sidemark / Job Name</p>
                <p className="text-white font-medium">{order.sidemark || '-'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">PO Number</p>
                <p className="text-white font-medium">{order.poNumber || order.po_number || '-'}</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-4 mb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-400 text-sm">Items</p>
                <p className="text-white font-medium">{order.items?.length || order.itemCount || '-'} Screen{(order.items?.length || 0) !== 1 ? 's' : ''}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Status</p>
                <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-green-900/30 text-green-400">
                  Payment Received
                </span>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-4">
            <div className="flex justify-between items-center mb-2">
              <p className="text-gray-400">Order Total</p>
              <p className="text-xl font-bold text-white">{formatCurrency(order.totalAmount || order.total_amount)}</p>
            </div>
            {order.paymentAmount || order.payment_amount ? (
              <div className="flex justify-between items-center">
                <p className="text-gray-400">Amount Paid</p>
                <p className="text-lg font-semibold text-green-400">{formatCurrency(order.paymentAmount || order.payment_amount)}</p>
              </div>
            ) : null}
            {(order.totalAmount || order.total_amount) && (order.paymentAmount || order.payment_amount) &&
              parseFloat(String(order.totalAmount || order.total_amount)) > parseFloat(String(order.paymentAmount || order.payment_amount)) ? (
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-700">
                <p className="text-gray-400">Balance Due</p>
                <p className="text-lg font-medium text-orange-400">
                  {formatCurrency(
                    parseFloat(String(order.totalAmount || order.total_amount)) -
                    parseFloat(String(order.paymentAmount || order.payment_amount))
                  )}
                </p>
              </div>
            ) : null}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-900/20 border border-blue-800 rounded-xl p-5 mb-6">
          <h3 className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            What Happens Next?
          </h3>
          <ul className="text-gray-300 text-sm space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">1.</span>
              <span>You'll receive an email confirmation with your order details</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">2.</span>
              <span>Our team will review and release your order for production</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">3.</span>
              <span>You can track your order status from the Orders page</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">4.</span>
              <span>We'll notify you when your order ships with tracking information</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to={`/orders/${orderId}`}
            className="flex-1 px-6 py-3 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors text-center"
          >
            View Order Details
          </Link>
          <Link
            to="/dashboard"
            className="flex-1 px-6 py-3 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors text-center"
          >
            Return to Dashboard
          </Link>
        </div>

        {/* Session ID (hidden, for debugging) */}
        {sessionId && (
          <p className="text-center text-gray-600 text-xs mt-6">
            Session: {sessionId.substring(0, 20)}...
          </p>
        )}
      </div>
    </div>
  );
}
